import { css, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../components-v2/Select';
import { THEME } from '../../../../../components-v2/Theme/theme';
import { ShiftDataSource } from '../../../../../pages/Volunteers/shiftDataSource';
import { AdditionalQuestions } from '../../../shared/components/AdditionalQuestions';
import { VolunteerShiftPickerPreview } from './VolunteerShiftPickerPreview';
import { VolunteerContactField, useVolunteerFormDraft } from './useVolunteerFormDraft';

const FIELD_LABELS: Record<VolunteerContactField['key'], string> = {
	name: 'Name',
	email: 'Email',
	phone: 'Phone',
	birthday: 'Birthday',
	address: 'Address',
};

const styles = StyleSheet.create({
	root: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.lg,
		marginTop: THEME.spacing.md,
	},
	notice: {
		backgroundColor: THEME.colors.info.light,
		borderLeft: `4px solid ${THEME.colors.info.main}`,
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.sm,
		padding: THEME.spacing.sm,
		borderRadius: THEME.common.borderRadius,
	},
	section: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.sm,
	},
	sectionHeading: {
		fontSize: THEME.typography.fontSizes.md,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.main,
		margin: 0,
	},
	fieldRow: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.md,
	},
	fieldLabel: {
		minWidth: 80,
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.sm,
	},
	levelTrigger: {
		minWidth: 140,
	},
});

type ContactFieldLevel = 'off' | 'optional' | 'required';

const LEVEL_ITEMS: { value: ContactFieldLevel; label: string }[] = [
	{ value: 'off', label: 'Off' },
	{ value: 'optional', label: 'Optional' },
	{ value: 'required', label: 'Required' },
];

const toLevel = (field: VolunteerContactField): ContactFieldLevel =>
	!field.collect ? 'off' : field.require ? 'required' : 'optional';

const fromLevel = (level: ContactFieldLevel): Pick<VolunteerContactField, 'collect' | 'require'> => ({
	collect: level !== 'off',
	require: level === 'required',
});

interface ContactFieldRowProps {
	field: VolunteerContactField;
	onLevelChange(level: ContactFieldLevel): void;
}

const ContactFieldRow: React.FC<ContactFieldRowProps> = ({ field, onLevelChange }) => (
	<div className={css(styles.fieldRow)}>
		<span className={css(styles.fieldLabel)}>{FIELD_LABELS[field.key]}</span>
		<Select
			disabled={field.locked}
			value={toLevel(field)}
			onValueChange={next => onLevelChange(next as ContactFieldLevel)}
		>
			<SelectTrigger styles={[styles.levelTrigger]}>
				<SelectValue />
			</SelectTrigger>
			<SelectContent>
				{LEVEL_ITEMS.map(item => (
					<SelectItem key={item.value} value={item.value}>
						{item.label}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	</div>
);

export const VolunteerFormBuilder: React.FC<{ shiftSource: ShiftDataSource }> = ({ shiftSource }) => {
	const { contactFields, setContactField } = useVolunteerFormDraft();
	const volunteerQuestionsForm = useForm({ defaultValues: { customForm: { fields: [], name: 'volunteer' } } });

	return (
		<div className={css(styles.root)}>
			<div className={css(styles.notice)}>The volunteer form isn&apos;t saved yet — coming soon.</div>
			<section className={css(styles.section)}>
				<h4 className={css(styles.sectionHeading)}>Contact information</h4>
				{contactFields.map(field => (
					<ContactFieldRow
						key={field.key}
						field={field}
						onLevelChange={level => setContactField(field.key, fromLevel(level))}
					/>
				))}
			</section>
			<section className={css(styles.section)}>
				<h4 className={css(styles.sectionHeading)}>Questions</h4>
				<FormProvider {...volunteerQuestionsForm}>
					<AdditionalQuestions isEdit={false} />
				</FormProvider>
			</section>
			<section className={css(styles.section)}>
				<h4 className={css(styles.sectionHeading)}>Which shift(s)?</h4>
				<VolunteerShiftPickerPreview shifts={shiftSource.shifts} />
			</section>
		</div>
	);
};
